import React from 'react';
import { IconBaseProps } from '@react-icons/all-files';
import { FaArchive } from '@react-icons/all-files/fa/FaArchive';
import { FaBriefcase } from '@react-icons/all-files/fa/FaBriefcase';
import { FaCalendar } from '@react-icons/all-files/fa/FaCalendar';
import { FaCalendarCheck } from '@react-icons/all-files/fa/FaCalendarCheck';
import { FaChartBar } from '@react-icons/all-files/fa/FaChartBar';
import { FaClipboardList } from '@react-icons/all-files/fa/FaClipboardList';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaGasPump } from '@react-icons/all-files/fa/FaGasPump';
import { FaHardHat } from '@react-icons/all-files/fa/FaHardHat';
import { FaRegBuilding } from '@react-icons/all-files/fa/FaRegBuilding';
import { FaRulerVertical } from '@react-icons/all-files/fa/FaRulerVertical';
import { FaToolbox } from '@react-icons/all-files/fa/FaToolbox';
import { FaTractor } from '@react-icons/all-files/fa/FaTractor';
import { FaTrailer } from '@react-icons/all-files/fa/FaTrailer';
import { FaTruck } from '@react-icons/all-files/fa/FaTruck';
import { FaTruckPickup } from '@react-icons/all-files/fa/FaTruckPickup';
import { FaUnlock } from '@react-icons/all-files/fa/FaUnlock';
import { FaUser } from '@react-icons/all-files/fa/FaUser';
import { FaWifi } from '@react-icons/all-files/fa/FaWifi';
import { GiFactory } from '@react-icons/all-files/gi/GiFactory';
import { GiFuelTank } from '@react-icons/all-files/gi/GiFuelTank';
import { GrDocumentStore } from '@react-icons/all-files/gr/GrDocumentStore';
import { MdDashboard } from '@react-icons/all-files/md/MdDashboard';
import { MdWeb } from '@react-icons/all-files/md/MdWeb';
import {
  WandSparkles,
  Building2,
  FlaskConical,
  NotebookPen,
  Fuel,
  Wrench,
  Tractor,
  Clipboard,
  Cog,
  WifiIcon,
  LayoutDashboardIcon,
  SlidersHorizontalIcon,
  ToggleRight,
  Bell,
  HistoryIcon,
  InboxIcon,
  BellRingIcon,
  DollarSign,
  FolderSync,
  KeyRound,
  ChartNoAxesCombined,
  BookOpen,
  ExternalLink,
  ListChecks,
  FileCog,
  Plug,
  FileSpreadsheet,
} from 'lucide-react';
import { NavGroup } from '@/hooks/useNavGroups';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import QRHandlerPage from './QRHandlerPage';
import { TbBusinessplan } from '@react-icons/all-files/tb/TbBusinessplan';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { BilderitModuleId, MachineReportStatus } from '@/generated/codegen';
import EmptyLayout from '@/components/Layout/EmptyLayout';
import EmptyLayoutWithLogoutFooter from '@/components/Layout/EmptyLayoutWithLogoutFooter';
import { statusOptions } from '@/modules/reports/machineStatusLabels';

const equipmentOOSFilterQuery = `?${new URLSearchParams({
  last_status: statusOptions[MachineReportStatus.OutOfService],
}).toString()}`;

const getReviewerInboxPath = (pathname: string) => {
  const normalizedPath = pathname.replace(/\/$/, '');

  if (/\/rental(?:\/certifications)?$/.test(normalizedPath)) {
    return normalizedPath.replace(
      /\/rental(?:\/certifications)?$/,
      '/rental/my-reviews'
    );
  }

  return `${normalizedPath}/rental/my-reviews`;
};

export const globalHiddenRroutesDef: NavGroup[] = [
  {
    title: 'Bilderit Handlers',
    Icon: WandSparkles,
    navItems: [
      {
        path: '/login',
        name: 'Login redirect',
        analyticsModule: 'cuenta',
        Icon: MdWeb,
        exact: true,
        strict: true,
        trackPageview: false,
        component: () => <Redirect to="/" />,
      },
      {
        path: '/:url*(/+)',
        name: 'Remove trailing slash',
        analyticsModule: 'administracion',
        Icon: MdWeb,
        exact: true,
        strict: true,
        trackPageview: false,
        component: ({ location }: RouteComponentProps) => (
          <Redirect
            to={
              location.pathname.replace(/\/+$/, '') +
              location.search +
              location.hash
            }
          />
        ),
      },
      {
        path: '/choose_company',
        name: 'Elegi tu compañia',
        analyticsModule: 'cuenta',
        Icon: MdWeb,
        exact: true,
        component: React.lazy(() => import('@/modules/app/ChooseCompanyPage')),
        breadcrumbs: [{ title: 'Elegí tu compañia' }],
      },
      {
        path: '/(confirm-employee|notifications)',
        name: 'Notificaciones',
        analyticsModule: 'cuenta',
        Icon: MdWeb,
        exact: true,
        component: React.lazy(
          () => import('@/modules/app/EmployeeConfirmPage')
        ),
        breadcrumbs: [{ title: 'Notificaciones' }],
      },
      {
        // Identity-only deep links from push notifications (BIL-4922): the
        // payload carries only `/n/:type/:id`, and this page rebuilds the
        // canonical, context-prefixed URL with current routing grammar at
        // click time — so links delivered to devices survive grammar changes.
        path: '/n/:type/:id',
        name: 'Notificación',
        analyticsModule: 'cuenta',
        Icon: BellRingIcon,
        exact: true,
        component: React.lazy(
          () => import('@/modules/app/NotificationLandingPage')
        ),
        breadcrumbs: [{ title: 'Notificación' }],
      },
      {
        path: '/settings',
        name: 'Configuración',
        analyticsModule: 'cuenta',
        Icon: MdWeb,
        exact: true,
        component: React.lazy(() => import('@/modules/app/SettingsPage')),
        breadcrumbs: [{ title: 'Configuracion' }],
      },
      {
        path: '/profile',
        name: 'Perfil',
        analyticsModule: 'cuenta',
        Icon: MdWeb,
        exact: true,
        component: React.lazy(() => import('@/modules/app/ProfilePage')),
        breadcrumbs: [{ title: 'Perfil' }],
      },
      {
        path: '/create',
        name: 'Crear compañia',
        analyticsModule: 'administracion',
        exact: false,
        Icon: FaRegBuilding,
        component: React.lazy(() => import('@/modules/app/CreateCompanyPage')),
        layout: EmptyLayoutWithLogoutFooter,
      },
      {
        // Permiso para conectar un asistente de IA al MCP. Va sin permiso de
        // ruta, en el grupo que ve todo el mundo: quien no es super root
        // tiene que ver el aviso (y poder cancelar), no un 404. El nonce de
        // la conexión viaja en la query: el pageview sólo lleva la ruta.
        path: '/mcp/authorize',
        name: 'Conectar al MCP de Bilderit',
        analyticsModule: 'cuenta',
        Icon: Plug,
        exact: true,
        component: React.lazy(() => import('@/modules/mcp/McpAuthorizePage')),
        layout: EmptyLayout,
      },
    ],
  },
  {
    title: 'Bilderit Handlers SR',
    Icon: WandSparkles,
    navItems: [
      {
        exact: true,
        path: '/admin/maint-plans/:id',
        name: 'Plan de mantenimiento',
        analyticsModule: 'mantenimiento',
        component: React.lazy(
          () => import('@/modules/maintenance_plans/MaintenancePlanPage')
        ),
        breadcrumbs: [
          { title: 'Bilderit' },
          { title: 'Librería de Planes', href: '/admin/maint-plans' },
        ],
      },
      {
        exact: true,
        path: '/admin/maint-plans/new',
        name: 'Nuevo plan de mantenimiento',
        analyticsModule: 'mantenimiento',
        component: React.lazy(
          () => import('@/modules/maintenance_plans/NewMaintenancePlanPage')
        ),
        breadcrumbs: [
          { title: 'Bilderit' },
          { title: 'Planes', href: '/admin/maint-plans' },
          { title: 'Nuevo plan' },
        ],
      },
      {
        exact: true,
        path: '/admin/maint-plans/tags',
        name: 'Etiquetas de la librería de planes',
        analyticsModule: 'mantenimiento',
        component: React.lazy(
          () =>
            import('@/modules/maintenance_plans/AdminMaintenancePlanTagsSettingsPage')
        ),
        breadcrumbs: [
          { title: 'Bilderit' },
          { title: 'Librería de Planes', href: '/admin/maint-plans' },
          { title: 'Etiquetas' },
        ],
      },
      {
        exact: true,
        path: '/admin/users/:id',
        name: 'Ficha de usuario',
        analyticsModule: 'usuarios',
        component: React.lazy(
          () => import('@/modules/super_root/AdminUserDetailPage')
        ),
        breadcrumbs: [
          { title: 'Bilderit' },
          { title: 'Gestión de usuarios', href: '/admin/users' },
          { title: 'Ficha' },
        ],
      },
      {
        exact: true,
        path: '/admin/users/cognito/:username',
        name: 'Ficha de identidad de Cognito',
        analyticsModule: 'usuarios',
        component: React.lazy(
          () => import('@/modules/super_root/AdminUserDetailPage')
        ),
        breadcrumbs: [
          { title: 'Bilderit' },
          { title: 'Gestión de usuarios', href: '/admin/users' },
          { title: 'Ficha' },
        ],
      },
    ],
  },
];

export const globalNavRoutesDef: NavGroup[] = [
  {
    title: 'Bilderit',
    Icon: WandSparkles,
    navItems: [
      {
        name: 'Analítica de uso',
        Icon: ChartNoAxesCombined,
        items: [
          {
            path: '/admin/dashboard/companies',
            name: 'Compañías',
            analyticsModule: 'analitica',
            exact: true,
            Icon: FaRegBuilding,
            component: React.lazy(
              () => import('@/modules/super_root/CompaniesDashboardPage')
            ),
          },
          {
            path: '/admin/analytics/guide',
            name: 'Guía',
            analyticsModule: 'analitica',
            exact: true,
            Icon: BookOpen,
            component: React.lazy(
              () => import('@/modules/super_root/UsageAnalyticsGuidePage')
            ),
          },
          // The PostHog project that receives this build's events
          // (import-env.mjs emits the URL next to the key). Absent in keyless
          // builds (local dev, CI), so no dead link.
          ...(import.meta.env.VITE_APP_POSTHOG_PROJECT_URL
            ? [
                {
                  name: 'PostHog',
                  Icon: ExternalLink,
                  externalHref: String(
                    import.meta.env.VITE_APP_POSTHOG_PROJECT_URL
                  ),
                },
              ]
            : []),
        ],
      },
      {
        path: '/create',
        name: 'Crear compañia',
        analyticsModule: 'administracion',
        exact: false,
        Icon: FaRegBuilding,
        component: React.lazy(() => import('@/modules/app/CreateCompanyPage')),
      },
      {
        path: '/admin/companies',
        name: 'Gestión de compañías',
        analyticsModule: 'administracion',
        exact: false,
        Icon: FaRegBuilding,
        component: React.lazy(
          () => import('@/modules/super_root/AdminCompaniesPage')
        ),
      },
      {
        path: '/admin/modules',
        name: 'Acceso a módulos',
        analyticsModule: 'administracion',
        exact: false,
        Icon: FaUnlock,
        component: React.lazy(
          () => import('@/modules/super_root/AdminModulesPage')
        ),
      },
      {
        path: '/admin/users',
        name: 'Gestión de usuarios',
        analyticsModule: 'usuarios',
        exact: false,
        Icon: FaUser,
        component: React.lazy(
          () => import('@/modules/super_root/AdminUsersPage')
        ),
      },
      {
        path: '/admin/tm-links',
        name: 'Gestión de telemetría',
        analyticsModule: 'telemetria',
        exact: false,
        Icon: FaWifi,
        component: React.lazy(
          () => import('@/modules/super_root/AdminTelemetryPage')
        ),
      },
      {
        path: '/admin/fleet-sync',
        name: 'Sincronización de flota',
        analyticsModule: 'telemetria',
        exact: false,
        Icon: FolderSync,
        component: React.lazy(
          () => import('@/modules/super_root/FleetSyncPage')
        ),
      },
      {
        path: '/admin/tools',
        name: 'Herramientas',
        analyticsModule: 'administracion',
        exact: false,
        Icon: FaToolbox,
        component: React.lazy(
          () => import('@/modules/super_root/AdminToolsPage')
        ),
      },
      {
        path: '/admin/validar-padron',
        name: 'Validar padrón',
        analyticsModule: 'administracion',
        exact: false,
        Icon: FileSpreadsheet,
        component: React.lazy(
          () => import('@/modules/roster/RosterValidatePage')
        ),
      },
      {
        path: '/admin/maint-plans',
        name: 'Librería de planes',
        analyticsModule: 'mantenimiento',
        exact: true,
        Icon: MdDashboard,
        component: React.lazy(
          () =>
            import('@/modules/maintenance_plans/MaintenancePlansLibraryPage')
        ),
      },
      {
        path: '/admin/consumables',
        name: 'Librería de insumos',
        analyticsModule: 'mantenimiento',
        exact: false,
        Icon: MdDashboard,
        component: React.lazy(
          () => import('@/modules/consumables/AdminConsumablesPage')
        ),
      },
      {
        path: '/admin/rental-parameters',
        name: 'Parámetros de alquiler',
        analyticsModule: 'rental',
        exact: false,
        Icon: SlidersHorizontalIcon,
        component: React.lazy(
          () => import('@/modules/super_root/AdminConfigParametersPage')
        ),
      },
      {
        path: '/admin/system-flags',
        name: 'System Flags',
        analyticsModule: 'administracion',
        exact: false,
        Icon: ToggleRight,
        component: React.lazy(
          () => import('@/modules/super_root/AdminSystemFlagsPage')
        ),
      },
      {
        path: '/admin/push',
        name: 'Push',
        analyticsModule: 'administracion',
        exact: false,
        Icon: Bell,
        component: React.lazy(
          () => import('@/modules/super_root/AdminPushPage')
        ),
      },
      {
        // Diagnostics bench for the device QA matrix (BIL-5346), which decides
        // whether it stays as a support tool.
        path: '/admin/push-bench',
        name: 'Push Bench',
        analyticsModule: 'administracion',
        exact: false,
        Icon: FlaskConical,
        component: React.lazy(
          () => import('@/modules/super_root/PushBenchPage')
        ),
      },
      {
        path: '/admin/public-api-keys',
        name: 'API keys públicas',
        analyticsModule: 'administracion',
        exact: false,
        Icon: KeyRound,
        component: React.lazy(
          () => import('@/modules/super_root/AdminPublicApiClientsPage')
        ),
      },
    ],
  },
];

export const companyHiddenRoutesDef: NavGroup[] = [
  {
    title: 'Company Handlers',
    Icon: Building2,
    navItems: [
      {
        path: '/:company?/:businessUnit?/:project?/reports/notices/tags',
        name: 'Etiquetas de novedades',
        analyticsModule: 'novedades',
        exact: true,
        component: React.lazy(
          () =>
            import('@/modules/machine_notices/MachineNoticeTagsSettingsPage')
        ),
        breadcrumbs: [
          { title: 'Seguimiento' },
          { title: 'Novedades', href: '/reports/notices' },
          { title: 'Etiquetas de novedades' },
        ],
      },
      {
        path: '/:company?/:businessUnit?/:project?/documents/tags',
        name: 'Etiquetas de documentos',
        analyticsModule: 'documentos',
        exact: true,
        component: React.lazy(
          () => import('@/modules/documents/DocumentTagsSettingsPage')
        ),
        breadcrumbs: [
          { title: 'Seguimiento' },
          { title: 'Documentos', href: '/documents' },
          { title: 'Etiquetas de documentos' },
        ],
      },
      {
        path: '/:company?/:businessUnit?/:project?/QR/:version/',
        component: QRHandlerPage,
        name: 'QR handler',
        analyticsModule: 'administracion',
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?/rental',
        name: 'Redirección de Revisiones',
        analyticsModule: 'rental',
        moduleId: BilderitModuleId.RentalCertification,
        routePermission: 'rental-certification:review',
        component: ({ location }: RouteComponentProps) => (
          <Redirect
            to={
              getReviewerInboxPath(location.pathname) +
              location.search +
              location.hash
            }
          />
        ),
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?/rental/certifications',
        name: 'Redirección de Certificaciones',
        analyticsModule: 'rental',
        moduleId: BilderitModuleId.RentalCertification,
        routePermission: 'rental-certification:review',
        component: ({ location }: RouteComponentProps) => (
          <Redirect
            to={
              getReviewerInboxPath(location.pathname) +
              location.search +
              location.hash
            }
          />
        ),
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?/rental/certifications/:id/editar',
        name: 'Editar Certificación (legacy)',
        analyticsModule: 'rental',
        moduleId: BilderitModuleId.RentalCertification,
        routePermission: 'rental-certification:review',
        component: ({ location }: RouteComponentProps) => (
          <Redirect to={location.pathname.replace(/\/editar$/, '')} />
        ),
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?/rental/certifications/:id',
        name: 'Detalle de Certificación',
        analyticsModule: 'rental',
        moduleId: BilderitModuleId.RentalCertification,
        component: React.lazy(
          () => import('@/modules/rental/CertificationDetailContainer')
        ),
        routePermission: 'rental-certification:review',
        breadcrumbs: [
          { title: 'Rental' },
          { title: 'Certificaciones', href: '/rental/certifications' },
          { title: 'Detalle' },
        ],
      },
      {
        path: '/:company?/:businessUnit?/:project?/settings',
        name: 'Configuración',
        analyticsModule: 'cuenta',
        Icon: MdWeb,
        exact: true,
        component: React.lazy(() => import('@/modules/app/SettingsPage')),
        breadcrumbs: [{ title: 'Configuración' }],
      },
      {
        path: '/:company?/:businessUnit?/:project?/profile',
        name: 'Perfil',
        analyticsModule: 'cuenta',
        Icon: MdWeb,
        exact: true,
        component: React.lazy(() => import('@/modules/app/ProfilePage')),
        breadcrumbs: [{ title: 'Perfil' }],
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?/(confirm-employee|notifications)',
        name: 'Notificaciones',
        analyticsModule: 'cuenta',
        component: React.lazy(
          () => import('@/modules/app/EmployeeConfirmPage')
        ),
        breadcrumbs: [{ title: 'Notificaciones' }],
      },
      {
        // Not a sidebar navItem — the four Equipos links (/machines, /trucks,
        // /vehicles, /fixed-assets) stay exactly as they are. This route
        // gives the unified equipment view's "Todos" tab a real, deep-linkable
        // URL so it gets its own correct breadcrumb instead of inheriting a
        // stale one from whichever of the four routes happened to render it.
        exact: true,
        path: '/:company?/:businessUnit?/:project?/equipos',
        name: 'Todos los equipos',
        analyticsModule: 'equipos',
        component: React.lazy(
          () => import('@/modules/machines/AllEquipmentPage')
        ),
        breadcrumbs: [{ title: 'Equipos' }, { title: 'Todos' }],
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?/machine/:id',
        name: 'Detalle de equipo',
        analyticsModule: 'equipos',
        component: React.lazy(
          () => import('@/modules/machines/MachineDetailPage')
        ),
        breadcrumbs: [{ title: 'Equipos' }, { title: 'Historial' }],
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?/machine/:id/documents',
        name: 'Documentos de equipo',
        analyticsModule: 'documentos',
        component: React.lazy(
          () => import('@/modules/documents/DocumentsByMachinePage')
        ),
        breadcrumbs: [{ title: 'Equipos' }, { title: 'Documentos de Equipo' }],
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?/machine/:id/telemetry',
        name: 'Telemetría de equipo',
        analyticsModule: 'telemetria',
        component: React.lazy(
          () => import('@/modules/telemetry/TelemetryByMachinePage')
        ),
        breadcrumbs: [{ title: 'Equipos' }, { title: 'Telemetría de Equipo' }],
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?/machine/:id/fuel',
        name: 'Control de combustible',
        analyticsModule: 'combustible',
        component: React.lazy(() => import('@/modules/fuel/FuelByMachinePage')),
        breadcrumbs: [
          { title: 'Equipos' },
          { title: 'Control de combustible' },
        ],
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?/:entity(machine|vehicle|truck)/:id/reports',
        name: 'Reportes',
        analyticsModule: 'partes_diarios',
        component: React.lazy(
          () => import('@/modules/reports/ReportsByMachinePage')
        ),
        breadcrumbs: [{ title: 'Equipos' }, { title: 'Reportes' }],
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?/reports/:entity(machines|trucks)/day/:day?',
        name: 'Reportes',
        analyticsModule: 'partes_diarios',
        component: React.lazy(
          () => import('@/modules/reports/ReportsByDayPage')
        ),
        breadcrumbs: [{ title: 'Reportes' }],
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?/maint-plans/new',
        name: 'Nuevo plan de mantenimiento',
        analyticsModule: 'mantenimiento',
        component: React.lazy(
          () => import('@/modules/maintenance_plans/NewMaintenancePlanPage')
        ),
        breadcrumbs: [
          { title: 'Mantenimiento' },
          { title: 'Planes', href: '/maint-plans' },
          { title: 'Nuevo plan' },
        ],
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?/maint-plans/tags',
        name: 'Etiquetas de planes de mantenimiento',
        analyticsModule: 'mantenimiento',
        component: React.lazy(
          () =>
            import('@/modules/maintenance_plans/MaintenancePlanTagsSettingsPage')
        ),
        breadcrumbs: [
          { title: 'Mantenimiento' },
          { title: 'Planes', href: '/maint-plans' },
          { title: 'Etiquetas' },
        ],
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?/maint-plans/:id',
        name: 'Plan de mantenimiento',
        analyticsModule: 'mantenimiento',
        component: React.lazy(
          () => import('@/modules/maintenance_plans/MaintenancePlanPage')
        ),
        breadcrumbs: [
          { title: 'Mantenimiento' },
          { title: 'Planes', href: '/maint-plans' },
        ],
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?/ot/new',
        name: 'Nueva orden de trabajo',
        analyticsModule: 'ordenes_de_trabajo',
        component: React.lazy(
          () => import('@/modules/work_orders/NewWorkOrderPage')
        ),
        breadcrumbs: [
          { title: 'Mantenimiento' },
          { title: 'Órdenes de trabajo', href: '/ot' },
          { title: 'Nuevo' },
        ],
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?/ot/tags',
        name: 'Etiquetas de órdenes de trabajo',
        analyticsModule: 'ordenes_de_trabajo',
        component: React.lazy(
          () => import('@/modules/work_orders/WorkOrderTagsSettingsPage')
        ),
        breadcrumbs: [
          { title: 'Mantenimiento' },
          { title: 'OTs', href: '/ot' },
          { title: 'Etiquetas' },
        ],
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?/ot/:id',
        name: 'Orden de trabajo',
        analyticsModule: 'ordenes_de_trabajo',
        component: React.lazy(
          () => import('@/modules/work_orders/WorkOrderPage')
        ),
        breadcrumbs: [
          { title: 'Mantenimiento' },
          { title: 'OTs', href: '/ot' },
        ],
      },
      {
        path: '/:company?/:businessUnit?/:project?',
        name: 'Estadísticas',
        analyticsModule: 'inicio',
        exact: true,
        Icon: FaChartBar,
        component: React.lazy(() => import('@/modules/app/DashboardPage')),
        breadcrumbs: [{ title: 'Estadísticas' }],
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?',
        name: 'Redirección de Revisor',
        analyticsModule: 'rental',
        routePermission: 'rental-certification:review',
        component: ({ location }: RouteComponentProps) => (
          <Redirect
            to={
              getReviewerInboxPath(location.pathname) +
              location.search +
              location.hash
            }
          />
        ),
      },
      {
        exact: true,
        path: '/:company?/:businessUnit?/:project?/checklists/templates/:templateId',
        name: 'Plantilla',
        analyticsModule: 'checklists',
        moduleId: BilderitModuleId.Checklists,
        component: React.lazy(
          () => import('@/modules/checklists/ChecklistTemplateEditorPage')
        ),
        breadcrumbs: [
          { title: 'Seguimiento' },
          { title: 'Checklists', href: '/checklists' },
          { title: 'Plantillas', href: '/checklists/templates' },
          { title: 'Plantilla' },
        ],
      },
      {
        // Not exact: the detail page mounts a nested route for the
        // notice-detail modal at `${match.url}/notice/:noticeId`.
        exact: false,
        path: '/:company?/:businessUnit?/:project?/checklists/:checklistId',
        name: 'Checklist',
        analyticsModule: 'checklists',
        moduleId: BilderitModuleId.Checklists,
        component: React.lazy(
          () => import('@/modules/checklists/ChecklistDetailPage')
        ),
        breadcrumbs: [
          { title: 'Seguimiento' },
          { title: 'Checklists', href: '/checklists' },
          { title: 'Checklist' },
        ],
      },
    ],
  },
];

export const companyNavRoutesDef: NavGroup[] = [
  {
    title: 'Compañía',
    Icon: Building2,
    navItems: [
      {
        path: '/:company?/:businessUnit?/:project?/company/edit',
        name: 'Setup',
        analyticsModule: 'administracion',
        exact: true,
        Icon: FaRegBuilding,
        component: React.lazy(() => import('@/modules/app/EditCompanyPage')),
        permission: 'sidebar:Compañía:Setup',
      },
      {
        path: '/:company?/:businessUnit?/:project?/business_units',
        name: 'Unidades de negocio',
        analyticsModule: 'proyectos',
        exact: false,
        Icon: TbBusinessplan,
        component: React.lazy(
          () => import('@/modules/business_units/BusinessUnitsPage')
        ),
        permission: 'sidebar:Compañía:Unidades de negocio',
      },
      {
        path: '/:company?/:businessUnit?/:project?/projects',
        name: 'Proyectos',
        analyticsModule: 'proyectos',
        exact: true,
        Icon: FaBriefcase,
        component: React.lazy(() => import('@/modules/projects/ProjectsPage')),
        permission: 'sidebar:Compañía:Proyectos',
      },
      {
        path: '/:company?/:businessUnit?/:project?/cost-centers',
        name: 'Centros de Costo',
        analyticsModule: 'proyectos',
        exact: true,
        Icon: FaClipboardList,
        component: React.lazy(
          () => import('@/modules/projects/ProjectCostCentersPage')
        ),
        permission: 'sidebar:Compañía:Centros de Costo',
      },
      {
        name: 'Archivo',
        Icon: FaBriefcase,
        items: [
          {
            path: '/:company?/:businessUnit?/:project?/company/archived/projects',
            name: 'Proyectos archivados',
            analyticsModule: 'proyectos',
            exact: true,
            Icon: FaBriefcase,
            component: React.lazy(
              () => import('@/modules/projects/ArchivedProjectsPage')
            ),
          },
          {
            path: '/:company?/:businessUnit?/:project?/company/archived/machines',
            name: 'Equipos archivados',
            analyticsModule: 'equipos',
            exact: true,
            Icon: FaArchive,
            component: React.lazy(
              () => import('@/modules/app/ArchivedEquipmentPage')
            ),
          },
        ],
        permission: 'sidebar:Compañía:Archivo',
      },
      {
        path: '/:company?/:businessUnit?/:project?/company/machines/settings',
        name: 'Configuración de equipos',
        analyticsModule: 'equipos',
        exact: false,
        Icon: FaTractor,
        component: React.lazy(
          () => import('@/modules/app/MachinesSettingsPage')
        ),
        permission: 'sidebar:Compañía:Equipos',
      },
      {
        path: '/:company?/:businessUnit?/:project?/employees',
        name: 'Usuarios',
        analyticsModule: 'usuarios',
        exact: true,
        Icon: FaHardHat,
        unauthorizedTooltip: 'No se pueden gestionar usuarios en la demo',
        component: React.lazy(
          () => import('@/modules/employees/EmployeesPage')
        ),
        permission: 'sidebar:Compañía:Usuarios',
      },
    ],
  },
  {
    title: 'Equipos',
    Icon: Tractor,
    navItems: [
      {
        path: '/:company?/:businessUnit?/:project?/machines',
        name: 'Equipos pesados',
        analyticsModule: 'equipos',
        exact: true,
        Icon: FaTractor,
        component: React.lazy(
          () => import('@/modules/heavy_equipment/HeavyEquipmentsPage')
        ),
      },
      {
        path: '/:company?/:businessUnit?/:project?/trucks',
        name: 'Camiones',
        analyticsModule: 'equipos',
        exact: true,
        Icon: FaTruck,
        component: React.lazy(() => import('@/modules/trucks/TrucksPage')),
      },
      {
        path: '/:company?/:businessUnit?/:project?/vehicles',
        name: 'Movilidades',
        analyticsModule: 'equipos',
        exact: true,
        Icon: FaTruckPickup,
        component: React.lazy(() => import('@/modules/vehicles/VehiclesPage')),
      },
      {
        path: '/:company?/:businessUnit?/:project?/fixed-assets',
        name: 'Activos fijos',
        analyticsModule: 'equipos',
        exact: true,
        Icon: FaTrailer,
        component: React.lazy(
          () => import('@/modules/fixed_assets/FixedAssetsPage')
        ),
      },
    ],
  },
  {
    title: 'Seguimiento',
    Icon: NotebookPen,
    alertCountKeys: [
      'expired_documents_count',
      'overdue_services_count',
      'open_critical_machine_notices_count',
    ],
    navItems: [
      {
        path: '/:company?/:businessUnit?/:project?/dashboard',
        name: 'Dashboard',
        analyticsModule: 'inicio',
        exact: true,
        Icon: FaChartBar,
        component: React.lazy(() => import('@/modules/app/DashboardPage')),
      },
      {
        path: '/:company?/:businessUnit?/:project?/services',
        name: 'Services',
        analyticsModule: 'mantenimiento',
        exact: true,
        Icon: FaCog,
        component: React.lazy(() => import('@/modules/services/ServicesPage')),
        alertCountKeys: ['overdue_services_count'],
      },
      {
        path: '/:company?/:businessUnit?/:project?/documents',
        name: 'Documentos',
        analyticsModule: 'documentos',
        exact: true,
        Icon: GrDocumentStore,
        component: React.lazy(
          () => import('@/modules/documents/DocumentsByCompanyPage')
        ),
        alertCountKeys: ['expired_documents_count'],
      },
      {
        path: '/:company?/:businessUnit?/:project?/reports/notices',
        name: 'Novedades',
        analyticsModule: 'novedades',
        exact: false,
        Icon: FaClipboardList,
        component: React.lazy(
          () => import('@/modules/machine_notices/MachineNoticesDashboardPage')
        ),
        alertCountKeys: ['open_critical_machine_notices_count'],
      },
      {
        // Clicking the sidebar entry goes straight to Resultados; Plantillas
        // stays reachable from the "…" menu (and from a link on the
        // Resultados page header). Other sidebar entries with sub-pages
        // ("Configuración", "Setup") are dropdown-only parents with no
        // destination of their own; this one is both a direct link and a
        // dropdown, so `items` here is only ever rendered as the "…" menu
        // (see nav-main.tsx), never as the collapsible-only case.
        path: '/:company?/:businessUnit?/:project?/checklists',
        name: 'Checklists',
        analyticsModule: 'checklists',
        exact: true,
        Icon: ListChecks,
        moduleId: BilderitModuleId.Checklists,
        permission: 'checklist:results:view',
        component: React.lazy(
          () => import('@/modules/checklists/ChecklistResultsPage')
        ),
        items: [
          {
            path: '/:company?/:businessUnit?/:project?/checklists/templates',
            name: 'Plantillas',
            analyticsModule: 'checklists',
            exact: true,
            Icon: FileCog,
            moduleId: BilderitModuleId.Checklists,
            permission: 'checklist:template:view',
            component: React.lazy(
              () => import('@/modules/checklists/ChecklistTemplatesPage')
            ),
          },
        ],
      },
    ],
  },
  {
    title: 'Rental',
    Icon: DollarSign,
    navItems: [
      {
        name: 'Configuración',
        Icon: FaCog,
        items: [
          {
            path: '/:company?/:businessUnit?/:project?/rental/configuration',
            name: 'Parámetros',
            analyticsModule: 'rental',
            exact: true,
            Icon: SlidersHorizontalIcon,
            permission: 'rental_params:project:edit',
            component: React.lazy(
              () =>
                import('@/modules/rental/parameters/RentalConfigurationPage')
            ),
          },
          {
            path: '/:company?/:businessUnit?/:project?/rental/notification-recipients',
            name: 'Notificaciones',
            analyticsModule: 'rental',
            exact: true,
            Icon: BellRingIcon,
            moduleId: BilderitModuleId.RentalCertification,
            permission: 'rental-certification:manage-copy-recipients',
            component: React.lazy(
              () =>
                import('@/modules/rental/notifications/CertificationNotificationRecipientsPage')
            ),
          },
        ],
      },
      {
        path: '/:company?/:businessUnit?/:project?/rental',
        name: 'Panel de Equipos',
        analyticsModule: 'rental',
        exact: true,
        Icon: FaCalendarCheck,
        component: React.lazy(
          () => import('@/modules/rental/dashboard/RentalDashboardPage')
        ),
      },
      {
        path: '/:company?/:businessUnit?/:project?/rental/certifications',
        name: 'Certificaciones',
        analyticsModule: 'rental',
        exact: true,
        Icon: HistoryIcon,
        moduleId: BilderitModuleId.RentalCertification,
        component: React.lazy(
          () =>
            import('@/modules/rental/certification/CertificationHistoryPage')
        ),
      },
      {
        path: '/:company?/:businessUnit?/:project?/rental/my-reviews',
        name: 'Mis Revisiones',
        analyticsModule: 'rental',
        exact: true,
        Icon: InboxIcon,
        moduleId: BilderitModuleId.RentalCertification,
        routePermission: 'rental-certification:review',
        component: React.lazy(
          () => import('@/modules/rental/review/ReviewerInboxPage')
        ),
      },
    ],
  },
  {
    title: 'Partes Diarios',
    Icon: Clipboard,
    navItems: [
      {
        path: '/:company?/:businessUnit?/:project?/reports',
        name: 'Tablero',
        analyticsModule: 'partes_diarios',
        exact: true,
        Icon: FaClipboardList,
        component: React.lazy(
          () => import('@/modules/reports/ReportsDashboardPage')
        ),
      },
      {
        path: '/:company?/:businessUnit?/:project?/reports/machines/day',
        skipRoute: true,
        name: 'Eq. pesados',
        analyticsModule: 'partes_diarios',
        Icon: FaCalendar,
        component: React.lazy(
          () => import('@/modules/reports/ReportsByDayPage')
        ),
      },
      {
        path: '/:company?/:businessUnit?/:project?/reports/trucks/day',
        skipRoute: true,
        name: 'Camiones',
        analyticsModule: 'partes_diarios',
        Icon: FaCalendar,
        component: React.lazy(
          () => import('@/modules/reports/ReportsByDayPage')
        ),
      },

      {
        path: '/:company?/:businessUnit?/:project?/reports/users',
        name: 'Operarios',
        analyticsModule: 'partes_diarios',
        exact: true,
        Icon: FaHardHat,
        component: React.lazy(
          () => import('@/modules/reports/MachinistReportsDashboardPage')
        ),
      },
    ],
  },
  {
    title: 'Combustible',
    Icon: Fuel,
    navItems: [
      {
        name: 'Setup',
        Icon: Cog,
        items: [
          {
            path: '/:company?/:businessUnit?/:project?/fuel/tanks',
            name: 'Almacenamiento',
            analyticsModule: 'combustible',
            exact: false,
            Icon: GiFuelTank,
            component: React.lazy(() => import('@/modules/fuel/FuelTanksPage')),
          },
          {
            path: '/:company?/:businessUnit?/:project?/fuel/providers',
            name: 'Proveedores',
            analyticsModule: 'combustible',
            exact: false,
            Icon: GiFactory,
            component: React.lazy(
              () => import('@/modules/fuel/FuelProvidersPage')
            ),
          },
          {
            path: '/:company?/:businessUnit?/:project?/fuel/consumers',
            name: 'Consumidores',
            analyticsModule: 'combustible',
            exact: false,
            Icon: FaTruck,
            component: React.lazy(
              () => import('@/modules/fuel/FuelConsumersPage')
            ),
          },
        ],
      },

      {
        path: '/:company?/:businessUnit?/:project?/fuel/dashboard',
        name: 'Dashboard',
        analyticsModule: 'combustible',
        exact: true,
        Icon: LayoutDashboardIcon,
        component: React.lazy(
          () => import('@/modules/fuel/dashboard/FuelDashboardPage')
        ),
      },
      {
        path: '/:company?/:businessUnit?/:project?/fuel/providers-dashboard',
        name: 'Resumen Proveedores',
        analyticsModule: 'combustible',
        exact: true,
        Icon: GiFactory,
        component: React.lazy(
          () => import('@/modules/fuel/ProvidersFuelDashboardPage')
        ),
      },
      {
        path: '/:company?/:businessUnit?/:project?/fuel/projects-dashboard',
        name: 'Resumen proyectos',
        analyticsModule: 'combustible',
        permission: 'debug:view',
        exact: true,
        Icon: FaBriefcase,
        component: React.lazy(
          () => import('@/modules/fuel/ProjectsFuelDashboardPage')
        ),
      },
      {
        path: '/:company?/:businessUnit?/:project?/fuel/tanks-dashboard',
        name: 'Control de Tanques',
        analyticsModule: 'combustible',
        exact: true,
        Icon: GiFuelTank,
        component: React.lazy(
          () => import('@/modules/fuel/TanksDashboardPage')
        ),
      },
      {
        path: '/:company?/:businessUnit?/:project?/fuel/machines-dashboard',
        name: 'Consumo Equipos',
        analyticsModule: 'combustible',
        exact: true,
        Icon: FaTractor,
        component: React.lazy(
          () => import('@/modules/fuel/MachinesFuelDashboardPage')
        ),
      },
      {
        path: '/:company?/:businessUnit?/:project?/fuel/dispatch',
        name: 'Despachos',
        analyticsModule: 'combustible',
        exact: false,
        Icon: FaGasPump,
        component: React.lazy(() => import('@/modules/fuel/FuelDispatchPage')),
      },
      {
        path: '/:company?/:businessUnit?/:project?/fuel/stock-control',
        name: 'Control de stock',
        analyticsModule: 'combustible',
        exact: false,
        Icon: (props: IconBaseProps) => (
          <FaRulerVertical viewBox="-128 0 512 512" {...props} />
        ),
        component: React.lazy(() => import('@/modules/fuel/FuelStockPage')),
      },
    ],
  },
  {
    title: 'Gestión de Taller',
    Icon: Wrench,
    navItems: [
      {
        path: '/:company?/:businessUnit?/:project?/dashboard-ot',
        name: 'Dashboard',
        analyticsModule: 'ordenes_de_trabajo',
        exact: true,
        Icon: FaHardHat,
        component: React.lazy(
          () => import('@/modules/work_orders/WorkOrdersDashboardPage')
        ),
      },
      {
        path: '/:company?/:businessUnit?/:project?/maint-plans/library',
        name: 'Librería de planes',
        analyticsModule: 'mantenimiento',
        exact: true,
        Icon: MdDashboard,
        component: React.lazy(
          () =>
            import('@/modules/maintenance_plans/MaintenancePlansLibraryPage')
        ),
      },
      {
        path: '/:company?/:businessUnit?/:project?/maint-plans',
        name: 'Planes de mantenimiento',
        analyticsModule: 'mantenimiento',
        exact: true,
        Icon: MdDashboard,
        component: React.lazy(
          () => import('@/modules/maintenance_plans/MaintenancePlansPage')
        ),
      },
      {
        path: '/:company?/:businessUnit?/:project?/ot',
        name: 'Ordenes de trabajo',
        analyticsModule: 'ordenes_de_trabajo',
        exact: true,
        Icon: FaHardHat,
        component: React.lazy(
          () => import('@/modules/work_orders/WorkOrdersPage')
        ),
      },
      {
        path: '/:company?/:businessUnit?/:project?/resources/consumables',
        name: 'Insumos',
        analyticsModule: 'mantenimiento',
        exact: true,
        Icon: FaChartBar,
        component: React.lazy(
          () => import('@/modules/consumables/ConsumablesPage')
        ),
      },
    ],
  },
  {
    title: 'B-Connect',
    Icon: WifiIcon,
    moduleId: BilderitModuleId.BconnectDashboard,
    navItems: [
      {
        path: '/:company?/:businessUnit?/:project?/telemetry',
        name: 'Dashboard Telemetría',
        analyticsModule: 'telemetria',
        label: (
          <div className="flex items-center space-between gap-2">
            <span className="whitespace-nowrap">Dashboard AEMP</span>{' '}
            <Tooltip>
              <TooltipContent>
                <p>Esta es una versión beta del dashboard AEMP.</p>
                <p>Comunicate con nosotros para más información.</p>
              </TooltipContent>
              <TooltipTrigger asChild>
                <div>
                  <Badge className="text-xs p-1">BETA</Badge>
                </div>
              </TooltipTrigger>
            </Tooltip>
          </div>
        ),
        exact: true,
        Icon: LayoutDashboardIcon,
        component: React.lazy(
          () => import('@/modules/telemetry/TelemetryDashboardPage')
        ),
      },
    ],
  },
];

export const companyCommandMenuRoutesDef: NavGroup[] = [
  {
    title: 'Atajos',
    permission: 'sidebar:Equipos',
    Icon: Tractor,
    navItems: [
      {
        path: `/:company?/:businessUnit?/:project?/machines${equipmentOOSFilterQuery}`,
        name: 'Equipos pesados fuera de servicio',
        analyticsModule: 'equipos',
        exact: true,
        Icon: FaTractor,
        component: React.lazy(
          () => import('@/modules/heavy_equipment/HeavyEquipmentsPage')
        ),
      },
      {
        path: `/:company?/:businessUnit?/:project?/trucks${equipmentOOSFilterQuery}`,
        name: 'Camiones fuera de servicio',
        analyticsModule: 'equipos',
        exact: true,
        Icon: FaTruck,
        component: React.lazy(() => import('@/modules/trucks/TrucksPage')),
      },
      {
        path: `/:company?/:businessUnit?/:project?/vehicles${equipmentOOSFilterQuery}`,
        name: 'Movilidades fuera de servicio',
        analyticsModule: 'equipos',
        exact: true,
        Icon: FaTruckPickup,
        component: React.lazy(() => import('@/modules/vehicles/VehiclesPage')),
      },
      {
        path: `/:company?/:businessUnit?/:project?/fixed-assets${equipmentOOSFilterQuery}`,
        name: 'Activos fijos fuera de servicio',
        analyticsModule: 'equipos',
        exact: true,
        Icon: FaTrailer,
        component: React.lazy(
          () => import('@/modules/fixed_assets/FixedAssetsPage')
        ),
      },
    ],
  },
];
